import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { getLucideIcon } from "../lib/icons";
import { getCellImage } from "../lib/api";
import { FileText, Sparkles, ArrowRight, HelpCircle } from "lucide-react";

const _imgCache = new Map(); // query(lowercase) -> photo doc (session cache)

export default function CellDetailModal({ open, onOpenChange, element, fromNotes, atMaxDepth, onDrill }) {
  const [img, setImg] = useState(null);
  const label = element?.label;

  useEffect(() => {
    if (!open || !label) return;
    const key = label.trim().toLowerCase();
    if (_imgCache.has(key)) { setImg(_imgCache.get(key)); return; }
    setImg(null);
    let cancelled = false;
    getCellImage(label)
      .then((d) => { _imgCache.set(key, d); if (!cancelled) setImg(d); })
      .catch(() => { if (!cancelled) setImg({ image_url: null }); });
    return () => { cancelled = true; };
  }, [open, label]);

  if (!element) return null;
  const Icon = getLucideIcon(element.icon_keyword);
  const detail = (element.detail && element.detail.trim()) || element.description;
  const hasPhoto = img && img.image_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cell-detail-modal" className="max-w-lg bg-[#0D0B18] border border-purple-500/25 text-white max-h-[85vh] overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">{element.label}</DialogTitle>
        <DialogDescription className="sr-only">Full detail for this sub-topic.</DialogDescription>

        {hasPhoto && (
          <div data-testid="cell-detail-image-wrap" className="rounded-xl overflow-hidden border border-purple-500/20 -mt-1">
            <img src={img.image_url} alt={element.label} data-testid="cell-detail-image"
              className="w-full h-40 object-cover" loading="lazy" />
            <div className="px-3 py-1.5 bg-[#07070E]/80 text-[10px] text-purple-300/70">
              Photo by{" "}
              <a href={img.photographer_url} target="_blank" rel="noreferrer" className="underline hover:text-purple-200" data-testid="cell-detail-photographer">{img.photographer_name}</a>{" "}
              on{" "}
              <a href={img.unsplash_url} target="_blank" rel="noreferrer" className="underline hover:text-purple-200">Unsplash</a>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          <div className="p-3 rounded-2xl bg-purple-500/15 border border-purple-500/25 shrink-0">
            <Icon className="w-8 h-8 text-purple-200" data-testid="cell-detail-icon" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-2xl font-extrabold text-white leading-tight">{element.label}</h3>
            {fromNotes && (
              <span className="mt-1 inline-flex text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/25 text-emerald-300 items-center gap-1">
                <FileText className="w-3 h-3" /> from your notes
              </span>
            )}
          </div>
        </div>

        <div className="mt-2 overflow-y-auto pr-1 flex-1 min-h-0 space-y-4">
          <div>
            <span className="font-mono-fx text-[10px] uppercase tracking-widest text-purple-400">Overview</span>
            <p className="mt-1 text-sm text-purple-100 leading-relaxed" data-testid="cell-detail-text">{detail}</p>
          </div>

          <div className="rounded-xl bg-purple-500/8 border border-purple-500/20 p-4">
            <span className="font-mono-fx text-[10px] uppercase tracking-widest text-purple-400 flex items-center gap-1.5">
              <HelpCircle className="w-3 h-3" /> Flashcard
            </span>
            <p className="mt-2 text-sm font-semibold text-white">{element.flashcard_question}</p>
            <p className="mt-1.5 text-sm text-emerald-200/90">{element.flashcard_answer}</p>
          </div>
        </div>

        {!atMaxDepth && onDrill && (
          <Button data-testid="cell-detail-drill-btn" onClick={() => { onDrill(element); onOpenChange(false); }}
            className="mt-2 w-full h-12 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow-[0_0_22px_rgba(168,85,247,0.4)]">
            <Sparkles className="w-4 h-4 mr-2" /> Drill into "{element.label}" <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
